import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUiStore } from '@/stores/ui-store';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * A right-side drawer pane that pushes (not overlays) the main content via
 * CSS grid. Two modes:
 *
 *   1. **Single-section** — pass `title` + `children`. The pane shows one
 *      titled section. Used for selection-driven detail panes.
 *   2. **Multi-section** — pass `sections` (and `activeSectionId` +
 *      `onActiveSectionChange`). The pane shows a tab strip in the header
 *      and a vertical stack of section labels when collapsed; clicking a
 *      collapsed label opens the pane to that section.
 *
 * Grid contract — the parent page sets up a grid like:
 *
 *   <div
 *     ref={gridRef}
 *     className="grid grid-cols-[1fr_var(--split-pane-w)]"
 *     style={{ '--split-pane-w': open ? `${width}px` : `${SPLIT_PANE_COLLAPSED_PX}px` }}
 *   >
 *     <main>...</main>
 *     <SplitPane open={...} width={...} gridRef={gridRef} ... />
 *   </div>
 *
 * The drag handle writes `--split-pane-w` directly on `gridRef.current`
 * during pointer-move (no React render per frame); the consumer's
 * `onCommitWidth` fires once on pointer-up so persistence happens once.
 *
 * Below `lg:` the pane hides itself — it's a desktop affordance. Pages
 * are expected to provide a single-column layout for narrow viewports.
 */

export interface SplitPaneSection {
  id: string;
  title: string;
  /** Section body. */
  content: ReactNode;
  /** Optional element rendered to the right of the section title in the
   *  open-state header (e.g. a "+ New thread" button). Click events on
   *  this element are stopped from bubbling to the tab. */
  headerAction?: ReactNode;
  /** Optional Lucide-style icon. Rendered above the vertical label when
   *  collapsed and beside the tab label when open. */
  icon?: ReactNode;
}

export interface SplitPaneProps {
  open: boolean;
  onToggle: () => void;
  /** Single-section mode: title shown in the header AND vertically when collapsed. */
  title?: string;
  /** Single-section mode: pane content rendered when open. */
  children?: ReactNode;
  /** Multi-section mode: array of sections to expose as tabs. When passed,
   *  `title` + `children` are ignored. */
  sections?: SplitPaneSection[];
  /** Multi-section mode: id of the currently visible section. Required if
   *  `sections` is passed. */
  activeSectionId?: string;
  /** Multi-section mode: called when the user clicks a tab (open) or a
   *  section label (collapsed — fires alongside an open). */
  onActiveSectionChange?: (id: string) => void;
  /** Current width in px (used as drag base + visible when open). */
  width: number;
  /** Called once on drag-end with the final width. Persistence lives here. */
  onCommitWidth: (px: number) => void;
  /** Ref to the CSS-grid container that owns `--split-pane-w`. */
  gridRef: RefObject<HTMLDivElement | null>;
  /** Lower bound for the drag (default 280). */
  minWidth?: number;
  /** Upper bound for the drag (default 640). */
  maxWidth?: number;
  /** CSS variable on `gridRef.current` to mutate during drag. */
  gridVarName?: string;
  /** Optional aria-label override (defaults to the localized open/close/resize label). */
  ariaLabelOpen?: string;
  ariaLabelClose?: string;
  ariaLabelResize?: string;
}

export const SPLIT_PANE_COLLAPSED_PX = 32;
export const SPLIT_PANE_DEFAULT_MIN = 280;
export const SPLIT_PANE_DEFAULT_MAX = 640;
export const SPLIT_PANE_DEFAULT_WIDTH = 380;
export const SPLIT_PANE_DEFAULT_VAR = '--split-pane-w';

/**
 * Hook for selection-driven panes (audit drilldown, candidate detail, etc.).
 * The pane's `open` state is driven by whether something is selected; only
 * the user-resized width is persisted across sessions per `key`.
 */
export function useResizablePane(key: string, defaultWidth = SPLIT_PANE_DEFAULT_WIDTH) {
  const width = useUiStore((s) => s.paneWidths[key] ?? defaultWidth);
  const setPaneWidth = useUiStore((s) => s.setPaneWidth);
  return {
    width,
    commitWidth: (px: number) => setPaneWidth(key, px),
  };
}

/**
 * Inline-style helper for the page grid container. Pass `open` and `width`,
 * spread the result onto the wrapper div's `style`. Avoids per-page
 * boilerplate around CSS-var typing.
 */
export function splitPaneGridStyle(open: boolean, width: number): CSSProperties {
  return {
    '--split-pane-w': open ? `${width}px` : `${SPLIT_PANE_COLLAPSED_PX}px`,
  } as CSSProperties;
}

export function SplitPane({
  open,
  onToggle,
  title,
  children,
  sections,
  activeSectionId,
  onActiveSectionChange,
  width,
  onCommitWidth,
  gridRef,
  minWidth = SPLIT_PANE_DEFAULT_MIN,
  maxWidth = SPLIT_PANE_DEFAULT_MAX,
  gridVarName = SPLIT_PANE_DEFAULT_VAR,
  ariaLabelOpen,
  ariaLabelClose,
  ariaLabelResize,
}: SplitPaneProps) {
  const lang = useUiStore((s) => s.lang);
  const isMulti = Array.isArray(sections) && sections.length > 0;
  const activeSection = isMulti
    ? sections!.find((s) => s.id === activeSectionId) ?? sections![0]!
    : null;
  // When the active tab changes — or the pane resizes / opens such
  // that the active button is now offscreen in the overflow scroll —
  // scroll it into view. Without this, switching to a tab off the
  // right edge would leave the user looking at unrelated tabs.
  // Respects `prefers-reduced-motion`: smooth scroll for users who
  // opt in to motion, instant for everyone else.
  const activeTabRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    activeTabRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [activeSection?.id, open, width]);

  if (!open) {
    return (
      <aside className="relative hidden min-h-0 flex-col items-stretch border-s border-[var(--color-border)] bg-[var(--color-surface)]/40 lg:flex">
        <BorderToggle
          onClick={onToggle}
          ariaLabel={ariaLabelOpen ?? t('splitPane.open', lang)}
          direction="open"
        />
        {isMulti ? (
          <div className="mt-10 flex flex-col items-stretch">
            {sections!.map((section) => {
              const active = section.id === activeSection?.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => {
                    onActiveSectionChange?.(section.id);
                    onToggle();
                  }}
                  title={section.title}
                  aria-label={section.title}
                  className={cn(
                    'flex flex-col items-center gap-1.5 py-3 text-sm text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-elevated)]/60 hover:text-[var(--color-ink)]',
                    active && 'text-[var(--color-accent)]',
                  )}
                >
                  {section.icon && (
                    <span aria-hidden className="opacity-90">
                      {section.icon}
                    </span>
                  )}
                  <span
                    style={{ writingMode: 'vertical-rl' }}
                    className="select-none"
                  >
                    {section.title}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div
            className="mt-10 select-none px-2 text-sm text-[var(--color-ink-muted)]"
            style={{ writingMode: 'vertical-rl' }}
          >
            {title}
          </div>
        )}
      </aside>
    );
  }

  return (
    <aside className="relative hidden min-h-0 flex-col lg:flex">
      <ResizeHandle
        gridRef={gridRef}
        gridVarName={gridVarName}
        currentWidth={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
        onCommit={onCommitWidth}
        ariaLabel={ariaLabelResize ?? t('splitPane.resize', lang)}
      />
      <BorderToggle
        onClick={onToggle}
        ariaLabel={ariaLabelClose ?? t('splitPane.close', lang)}
        direction="close"
      />
      <Card className="flex h-full flex-col rounded-none border-0">
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 px-3 py-2.5">
          {isMulti ? (
            // Two-column header: tablist scrolls horizontally when
            // tabs overflow the available width; `headerAction`
            // stays pinned on the inline-end so primary actions
            // (e.g. "+ New thread") never get hidden by a scroll.
            // `@container` still collapses inactive labels to icons
            // when the pane itself is narrow — that handles the
            // 1-2-tab dossier case; the scroll handles the
            // many-tabs graph case.
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <div
                role="tablist"
                aria-orientation="horizontal"
                className="@container flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {sections!.map((section) => {
                  const active = section.id === activeSection?.id;
                  return (
                    <button
                      key={section.id}
                      ref={active ? activeTabRef : undefined}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      aria-label={section.title}
                      title={section.title}
                      onClick={() => onActiveSectionChange?.(section.id)}
                      className={cn(
                        'inline-flex h-7 min-w-7 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-xs px-1.5 text-xs transition-colors',
                        active
                          ? 'font-medium text-[var(--color-ink)]'
                          : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]',
                      )}
                    >
                      {section.icon && (
                        <span aria-hidden className="opacity-80">
                          {section.icon}
                        </span>
                      )}
                      <span
                        className={cn(
                          active ? 'inline' : 'hidden @[300px]:inline',
                        )}
                      >
                        {section.title}
                      </span>
                    </button>
                  );
                })}
              </div>
              {activeSection?.headerAction && (
                <div
                  className="shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {activeSection.headerAction}
                </div>
              )}
            </div>
          ) : (
            <CardTitle className="text-sm text-[var(--color-ink)]">
              {title}
            </CardTitle>
          )}
        </CardHeader>
        <CardContent className="flex-1 min-h-0 overflow-hidden p-0">
          {isMulti ? activeSection?.content : children}
        </CardContent>
      </Card>
    </aside>
  );
}

/**
 * Open / close button rendered on the inline-start border of the pane —
 * a small "drawer pull" affordance. Click toggles the pane.
 *
 * In the open state this overlays the resize handle: pointer-down on the
 * button is stopped from bubbling so it doesn't kick off a drag, and
 * because the button has its own hit area at the very top of the strip,
 * users naturally drag from the rest of the handle line below.
 *
 * In the collapsed state there's no resize handle to share with, so the
 * button just sits at the top of the strip on its own.
 */
function BorderToggle({
  onClick,
  ariaLabel,
  direction,
}: {
  onClick: () => void;
  ariaLabel: string;
  direction: 'open' | 'close';
}) {
  const Icon = direction === 'open' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={cn(
        'absolute start-[-12px] top-3 z-20 flex size-6 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] shadow-sm transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-ink)]',
      )}
    >
      <Icon className="size-3.5 icon-directional" />
    </button>
  );
}

interface ResizeHandleProps {
  gridRef: RefObject<HTMLDivElement | null>;
  gridVarName: string;
  currentWidth: number;
  minWidth: number;
  maxWidth: number;
  onCommit: (px: number) => void;
  ariaLabel: string;
}

function ResizeHandle({
  gridRef,
  gridVarName,
  currentWidth,
  minWidth,
  maxWidth,
  onCommit,
  ariaLabel,
}: ResizeHandleProps) {
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    const startX = e.clientX;
    const startW = currentWidth;
    let lastW = startW;

    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    function compute(clientX: number): number {
      // Pane sits inline-end. In LTR that's the right; dragging the
      // (left) handle leftward grows the pane (delta = startX - clientX).
      // In RTL the pane is on the left and its inline-start edge is the
      // right side, so dragging rightward grows it (delta inverts).
      const delta = isRtl ? clientX - startX : startX - clientX;
      return Math.max(minWidth, Math.min(maxWidth, startW + delta));
    }

    function onMove(ev: PointerEvent) {
      const next = compute(ev.clientX);
      lastW = next;
      gridRef.current?.style.setProperty(gridVarName, `${next}px`);
    }
    function onUp() {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      onCommit(lastW);
    }
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      className="group absolute inset-y-0 start-0 z-10 w-1.5 cursor-col-resize touch-none"
    >
      <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[var(--color-border)] transition-colors group-hover:bg-[var(--color-accent)]" />
    </div>
  );
}
